import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useAddSchoolToForm,
  useCheckEligibility,
  useGetStaffCreatePrecheck,
  useGetStaffCreatePrecheckKey,
} from "src/generated/hooks/form-service/queries";
import type {
  ApplicantSummary,
  EligibleFormTemplateSummary,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { PersonAddress, PersonAddressSchema } from "src/schemas/Address";
import { GET_SCHOOLS_FOR_ORGANIZATION } from "src/services/eligibility/graphql/queries";
import { Answer } from "src/services/formTemplate";
import {
  findAddressQuestion,
  getGradesAnswer,
} from "src/services/formTemplate/preRankingSection/determineEligibility";
import { useSchoolRankingSectionRemoteData } from "src/services/formTemplate/useFormTemplateSection";
import { Forms } from "src/services/url/OrgAdmin";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import {
  CreateFormFormValues,
  CreateFormSelectedApplicant,
  CreateFormSelectedTemplate,
  useCreateFormSubmit,
} from "./useCreateFormSubmit";
import { useCreateStaffFormResponse } from "./useCreateStaffFormResponse";

// Single-select answers (option ids) keyed by question id, for the eligibility
// endpoint's question-based check.
function toEligibilityAnswers(
  answers: Answer.FormikValues
): Record<string, string | null> {
  return Object.fromEntries(
    Object.entries(answers).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string"
    )
  );
}

// The address answer for the location-boundaries question, for the eligibility
// endpoint's location check. Geocoding happens server-side.
const EligibilityAddressSchema = PersonAddressSchema.partial();

function toEligibilityAddress(
  answers: Answer.FormikValues,
  questions: readonly AF.Question<AF.WithId>[]
): Partial<PersonAddress> | undefined {
  const { question } = findAddressQuestion(questions);
  if (!question) {
    return undefined;
  }
  const parsed = EligibilityAddressSchema.safeParse(answers[question.id]);
  return parsed.success ? parsed.data : undefined;
}

interface UseCreateFormForTemplateDialogParams {
  enrollmentPeriodId: uuid;
  formTemplateId: uuid;
  formTemplateName: string;
  isOpen: boolean;
  onClose: () => void;
  // A just-created student to preselect on open (Add family round trip). Seeded
  // into selectedApplicant so the eligibility/precheck queries fire for it.
  initialSelectedStudent?: ApplicantSummary | null;
}

/**
 * Holds all state, data fetching, and submit handlers for
 * CreateFormForTemplateDialog so the component itself is just the view. Every
 * returned value preserves the behavior of the original inline implementation.
 */
export function useCreateFormForTemplateDialog({
  enrollmentPeriodId,
  formTemplateId,
  formTemplateName,
  isOpen,
  onClose,
  initialSelectedStudent,
}: UseCreateFormForTemplateDialogParams) {
  const organization = useOrganization();
  const organizationId = React.useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Render the dialog's own copy through the org glossary so a renamed "school"
  // or "student" shows the org's term, matching the rest of the app.
  const { glossaryTransform } = useGlossary();

  const { isSchoolAdmin, applyingSchoolIds, allSchoolIds } = useSchoolAdmin();
  // PR-1: the create-new-student row only shows for staff who can create a student.
  const canCreateStudent = useUserPermissions().hasOne("user:create");
  // Schools the staffer can create a response for (PR-3). Prefer applying-admin
  // schools; fall back to all assigned schools.
  const staffSchoolIds = React.useMemo(
    () => (applyingSchoolIds.length > 0 ? applyingSchoolIds : allSchoolIds),
    [applyingSchoolIds, allSchoolIds]
  );

  const [selectedFormTemplate, setSelectedFormTemplate] =
    React.useState<CreateFormSelectedTemplate>(undefined);
  const [selectedApplicant, setSelectedApplicant] =
    React.useState<CreateFormSelectedApplicant>(undefined);
  const [phase, setPhase] = React.useState<"student" | "preRanking">("student");
  const [selectedSchoolId, setSelectedSchoolId] = React.useState<uuid>("");
  const [isNotEligibleOpen, setIsNotEligibleOpen] = React.useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] =
    React.useState(false);

  React.useEffect(() => {
    if (isOpen && formTemplateId && formTemplateName) {
      const template: EligibleFormTemplateSummary = {
        id: formTemplateId,
        name: formTemplateName,
        description: null,
        active: true,
        locked: false,
        can_start: false,
      };
      setSelectedFormTemplate(template);
    }
  }, [isOpen, formTemplateId, formTemplateName]);

  const resetDialogState = React.useCallback(() => {
    setSelectedFormTemplate(null);
    setSelectedApplicant(null);
    setPhase("student");
    setSelectedSchoolId("");
    setIsNotEligibleOpen(false);
  }, []);

  // The dialog stays mounted between opens, so reset every time it closes to
  // guarantee a clean slate on reopen regardless of which path closed it.
  React.useEffect(() => {
    if (!isOpen) {
      resetDialogState();
    }
  }, [isOpen, resetDialogState]);

  const handleClose = React.useCallback(
    (resetForm: () => void) => {
      resetForm();
      resetDialogState();
      onClose();
    },
    [onClose, resetDialogState]
  );

  const staffOnClose = React.useCallback(() => {
    resetDialogState();
    onClose();
  }, [resetDialogState, onClose]);

  const {
    handleSubmitForm,
    getAssignedFormsRD,
    isEligibleTemplatesLoading,
    alertText,
    alertStatus,
    showSeeFormLink,
    canStartForm,
    handleOnSeeFormClick,
    isSubmitting,
  } = useCreateFormSubmit({
    selectedEnrollmentPeriod: enrollmentPeriodId
      ? { id: enrollmentPeriodId }
      : undefined,
    selectedApplicant,
    selectedFormTemplate,
    onClose: handleClose,
  });

  const schoolRankingSectionRD =
    useSchoolRankingSectionRemoteData(formTemplateId);
  const schoolRankingSectionId = schoolRankingSectionRD.toNullable()?.id;

  const { submit: submitStaffResponse, isSubmitting: isStaffSubmitting } =
    useCreateStaffFormResponse({
      formTemplateId,
      applicantId: selectedApplicant?.id ?? "",
      onClose: staffOnClose,
    });

  const { mutateAsync: checkEligibility } = useCheckEligibility();
  const { mutateAsync: addSchoolToForm } = useAddSchoolToForm();
  const queryClient = useQueryClient();

  // Detect an existing response (and its ranked count / max) server-side; no form
  // data is returned.
  const {
    data: precheckResponse,
    isFetching: isPrecheckFetching,
    isPlaceholderData: isPrecheckPlaceholder,
  } = useGetStaffCreatePrecheck(
    {
      query: {
        personId: selectedApplicant?.id ?? "",
        formTemplateId,
        ...(selectedSchoolId ? { schoolId: selectedSchoolId } : {}),
      },
    },
    undefined,
    {
      enabled: isSchoolAdmin && !!selectedApplicant,
      // Keep the prior result while refetching after a school change so the dialog
      // does not flash between states (existingFormId stays stable).
      placeholderData: (previousData) => previousData,
    }
  );
  // The precheck decides create-new vs add-to-existing. Until it has resolved for
  // the selected applicant (data is neither loading nor a placeholder from the
  // previous applicant), the staffer must not advance into the create-new flow.
  const isPrecheckPending =
    isSchoolAdmin &&
    !!selectedApplicant &&
    (isPrecheckFetching || isPrecheckPlaceholder);
  const existingFormId = precheckResponse?.data?.existingFormId ?? null;
  const rankedSchoolCount = precheckResponse?.data?.rankedSchoolCount ?? 0;
  const precheckMaxSchools = precheckResponse?.data?.maxSchools ?? null;
  const selectedSchoolOnForm =
    precheckResponse?.data?.selectedSchoolOnForm ?? false;

  // Once an applicant is selected, every decision-driving query must finish before
  // any branch renders, so the modal never flashes an intermediate state. While
  // this is true, the body shows only a spinner.
  const isDecisionLoading =
    !!selectedApplicant &&
    (isPrecheckPending ||
      isEligibleTemplatesLoading ||
      getAssignedFormsRD.isLoading());

  // School names for the selector (not form data).
  const { remoteData: schoolsRemoteData } = useRemoteDataQuery<
    GQL.GetSchoolsForOrganization,
    GQL.GetSchoolsForOrganizationVariables
  >(GET_SCHOOLS_FOR_ORGANIZATION, {
    variables: {
      organization_id: organizationId,
      enrollment_period_id: enrollmentPeriodId,
    },
    skip: !organizationId || !enrollmentPeriodId,
  });
  const staffSchoolOptions = React.useMemo(
    () =>
      schoolsRemoteData
        .map((data) => data.school)
        .withDefault([])
        .filter((school) => staffSchoolIds.includes(school.id)),
    [schoolsRemoteData, staffSchoolIds]
  );

  // A school admin with no school offered in this enrollment period can never
  // satisfy the required school field, so the form can't be created. Gated on the
  // schools query having resolved so it doesn't flash while loading.
  const hasNoStaffSchools =
    isSchoolAdmin &&
    schoolsRemoteData.hasData() &&
    staffSchoolOptions.length === 0;

  // Preselect the staffer's only school; reruns on each open so it survives reopen.
  React.useEffect(() => {
    if (isOpen && staffSchoolOptions.length === 1) {
      setSelectedSchoolId(staffSchoolOptions[0]?.id ?? "");
    }
  }, [isOpen, staffSchoolOptions]);

  const initialValues: CreateFormFormValues = React.useMemo(
    () => ({
      organizationId,
      enrollmentPeriodId,
      formTemplateId,
      // Seed the applicant here (not only via the picker's mount-time setValue) so
      // that when organizationId resolves asynchronously and changes initialValues,
      // enableReinitialize preserves the prefilled student instead of clearing it.
      applicantId: initialSelectedStudent?.id ?? "",
    }),
    [
      organizationId,
      enrollmentPeriodId,
      formTemplateId,
      initialSelectedStudent?.id,
    ]
  );

  const onSelectedApplicant = React.useCallback(
    (applicant: ApplicantSummary | null | undefined) => {
      setSelectedApplicant(applicant);
      // Selecting or clearing the student returns the modal to a fresh decision
      // state so no prior phase or not-eligible alert lingers.
      setPhase("student");
      setIsNotEligibleOpen(false);
    },
    []
  );

  const applicantName = selectedApplicant
    ? `${selectedApplicant.first_name} ${selectedApplicant.last_name}`
    : "This student";

  const preRankingApplicant = React.useMemo(
    () => ({
      birth_date: selectedApplicant?.birth_date ?? null,
      first_name: selectedApplicant?.first_name ?? null,
    }),
    [selectedApplicant]
  );

  // PR-7/PR-8: the student already has a response (detected server-side). For a
  // school admin this is the add-to-existing branch.
  const isAddToExisting =
    isSchoolAdmin && !!existingFormId && !!selectedApplicant;
  const isMaxReached =
    precheckMaxSchools != null &&
    precheckMaxSchools > 0 &&
    rankedSchoolCount >= precheckMaxSchools;

  const navigateToForm = React.useCallback(
    (formId: uuid) => {
      // This is a workaround to fix the issue where the modal is not closed before
      // the form is opened, which will disable scrolling on the form detail page.
      // This is the existing pattern used in useCreateFormSubmit.
      setTimeout(() => {
        navigate(
          organization
            .map(() =>
              Forms.view({
                organization,
                id: formId,
                formTemplateId,
                params: searchParams.toString(),
              })
            )
            .withDefault("#")
        );
      }, 100);
    },
    [navigate, organization, formTemplateId, searchParams]
  );

  // Create-new: check eligibility via the endpoint against the captured answers,
  // then create the response.
  const handlePreRankingSubmit = React.useCallback(
    async (
      answers: Answer.FormikValues,
      questions: readonly AF.Question<AF.WithId>[]
    ) => {
      if (!selectedSchoolId || !schoolRankingSectionId) {
        return;
      }
      setIsCheckingEligibility(true);
      try {
        const response = await checkEligibility({
          body: {
            data: {
              formTemplateId,
              schoolIds: [selectedSchoolId],
              answers: toEligibilityAnswers(answers),
              gradeConfigId: getGradesAnswer(questions, answers) ?? null,
              address: toEligibilityAddress(answers, questions),
            },
          },
        });
        const eligible =
          response.data?.data.eligibleSchoolIds.includes(selectedSchoolId) ??
          false;
        if (!eligible) {
          setIsNotEligibleOpen(true);
          return;
        }
        await submitStaffResponse({
          answers,
          questions,
          schoolId: selectedSchoolId,
          schoolRankingSectionId,
        });
      } catch (error) {
        console.error(error);
        toast({
          title: "Could not check eligibility",
          description: "Please try again.",
          status: "error",
          isClosable: true,
        });
      } finally {
        setIsCheckingEligibility(false);
      }
    },
    [
      selectedSchoolId,
      schoolRankingSectionId,
      formTemplateId,
      checkEligibility,
      submitStaffResponse,
      toast,
    ]
  );

  // Add-to-existing: eligibility is checked server-side against the existing
  // form's answers (fetched elevated in the endpoint), then the school is added.
  const handleAddToExisting = React.useCallback(async () => {
    // existingFormId can be a placeholder from the previously selected applicant
    // while the precheck refetches; acting on it would target the wrong form.
    if (!selectedSchoolId || !existingFormId || isPrecheckPending) {
      return;
    }
    setIsCheckingEligibility(true);
    try {
      const eligibilityResponse = await checkEligibility({
        body: {
          data: {
            formTemplateId,
            schoolIds: [selectedSchoolId],
            formId: existingFormId,
          },
        },
      });
      const eligible =
        eligibilityResponse.data?.data.eligibleSchoolIds.includes(
          selectedSchoolId
        ) ?? false;
      if (!eligible) {
        setIsNotEligibleOpen(true);
        return;
      }
      const addResponse = await addSchoolToForm({
        body: { data: { formId: existingFormId, schoolId: selectedSchoolId } },
      });
      const result = addResponse.data?.data.result;
      if (result === "max-reached") {
        toast({
          title: "Could not add school",
          description:
            "This response has reached the maximum number of ranked schools.",
          status: "error",
          isClosable: true,
        });
        return;
      }
      // The precheck is cached (global 5-minute staleTime); invalidate it so a
      // later open for this student reflects the just-added school.
      await queryClient.invalidateQueries({
        queryKey: [useGetStaffCreatePrecheckKey],
      });
      staffOnClose();
      toast({
        title:
          result === "already-on-form"
            ? "Opening existing form"
            : "Form created",
        status: "success",
        isClosable: true,
      });
      navigateToForm(existingFormId);
    } catch (error) {
      console.error(error);
      toast({
        title: "Could not add school",
        description: "Please try again.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsCheckingEligibility(false);
    }
  }, [
    selectedSchoolId,
    existingFormId,
    isPrecheckPending,
    formTemplateId,
    checkEligibility,
    addSchoolToForm,
    queryClient,
    staffOnClose,
    navigateToForm,
    toast,
  ]);

  // Brand-new response (after "Next"): the pre-ranking capture owns its own
  // Formik state, so it must stay mounted even while the not-eligible alert shows
  // (it is only hidden), otherwise returning would wipe the captured answers.
  const inCreatePreRanking =
    isSchoolAdmin && phase === "preRanking" && !isAddToExisting;

  // School admins never create via the outer form submit; they use the staff flow.
  // Neutralize the outer submit so an Enter keypress cannot create a bare form.
  const handleOuterSubmit = isSchoolAdmin ? async () => {} : handleSubmitForm;

  const isBusy = isStaffSubmitting || isCheckingEligibility;

  return {
    organizationId,
    isSchoolAdmin,
    glossaryTransform,
    initialValues,
    onSubmit: handleOuterSubmit,
    isDecisionLoading,
    inCreatePreRanking,
    isBusy,
    student: {
      onSelect: onSelectedApplicant,
      name: applicantName,
      preRankingApplicant,
      canCreateStudent,
      // Context carried to the Add family page so it can return here afterward.
      createNewStudentReturn: {
        formTemplateId,
        enrollmentPeriodId,
        params: searchParams.toString(),
      },
    },
    school: {
      options: staffSchoolOptions,
      selectedId: selectedSchoolId,
      setSelectedId: setSelectedSchoolId,
      hasNoStaffSchools,
    },
    precheck: {
      isAddToExisting,
      isMaxReached,
      selectedSchoolOnForm,
      existingFormId,
      isPending: isPrecheckPending,
    },
    notEligible: {
      isOpen: isNotEligibleOpen,
      setIsOpen: setIsNotEligibleOpen,
    },
    // Create-new (non-school-admin) alert state, from useCreateFormSubmit.
    createNew: {
      assignedFormsRD: getAssignedFormsRD,
      alertText,
      alertStatus,
      showSeeFormLink,
      canStartForm,
      onSeeFormClick: handleOnSeeFormClick,
      isSubmitting,
    },
    actions: {
      setPhase,
      close: handleClose,
      staffClose: staffOnClose,
      submitPreRanking: handlePreRankingSubmit,
      addToExisting: handleAddToExisting,
      navigateToForm,
    },
  };
}
